import { useGetbill } from "@/api/billing/get-billing";
import { useGetBillingHistory, BillingHistoryItem } from "@/api/billing/get-billing-history";
import PageWrapper from "@/components/layout/page-wrapper";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useParams } from "react-router";
import NoDataFound from "@/components/layout/no-data-found";

const BillingDetails = () => {
    const { id } = useParams();
    const { data: billingData } = useGetbill(id || "");
    
    // Extract store ID from billing data once loaded
    // Assuming billing data has structure like { data: { store_id: string, ... } }
    const storeId = billingData?.data?.store_id || id || "";
    const { data: billingHistoryData, isLoading: billingHistoryLoading } = useGetBillingHistory(storeId);

    // Get billing history items from the API response
    const billingHistory = billingHistoryData?.data || [];

    return (
        // Main container to center the component on the page
        <PageWrapper pageTitle="Bill Details" currentPage="Bill Details">
            {/* The main card component for plan details */}
            <div className="w-full">
                <Card>
                    {/* Card Header: Contains the title and action buttons */}
                    <CardHeader className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-gray-200 space-y-3 sm:space-y-0">
                        <CardTitle className="text-gray-600">Plan details</CardTitle>
                    </CardHeader>

                    {/* Card Content: Contains the core plan information */}
                    <CardContent className="pt-6">
                        <div className="flex items-center mb-6">
                            <h2 className="text-xl font-bold text-gray-900">{billingData?.data?.plan_name || "Plan"}</h2>
                            <Badge
                                className={`ml-3 ${
                                    billingData?.data?.is_active
                                        ? "bg-green-100 text-green-800 border-green-200"
                                        : "bg-red-100 text-red-800 border-red-200"
                                }`}
                            >
                                <span
                                    className={`w-2 h-2 rounded-full mr-1.5 ${billingData?.data?.is_active ? "bg-green-500" : "bg-red-500"}`}
                                ></span>
                                {billingData?.data?.is_active ? "Active" : "Inactive"}
                            </Badge>
                        </div>

                        {/* Grid for start and billing dates */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                            <div>
                                <p className="text-sm text-gray-500">Start date</p>
                                <p className="text-base font-medium text-gray-800">
                                    {billingData?.data?.created_at ? new Date(billingData.data.created_at).toLocaleDateString() : "N/A"}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Next Billing Date</p>
                                <p className="text-base font-medium text-gray-800">
                                    {billingData?.data?.next_billing_date ? new Date(billingData.data.next_billing_date).toLocaleDateString() : "N/A"}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Current Period End</p>
                                <p className="text-base font-medium text-gray-800">
                                    {billingData?.data?.current_period_end
                                        ? new Date(billingData.data.current_period_end).toLocaleDateString()
                                        : "N/A"}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="mt-4 p-4">
                    <h3 className="text-gray-600 font-semibold">Billing History</h3>
                    <Separator className="my-4" />
                    {billingHistoryLoading ? (
                        <div className="flex justify-center items-center h-64">
                            <p className="text-gray-600">Loading billing history...</p>
                        </div>
                    ) : billingHistory.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64 py-4">
                            <NoDataFound message="No billing history available" />
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>
                                        <Checkbox />
                                    </TableHead>
                                    <TableHead>Invoice Number</TableHead>
                                    <TableHead>Description</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Created Date</TableHead>
                                    <TableHead>Due Date</TableHead>
                                    <TableHead>Amount</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {billingHistory.map((invoice: BillingHistoryItem) => (
                                    <TableRow key={invoice.invoice_id} className="hover:bg-muted/20">
                                        <TableCell>
                                            <Checkbox />
                                        </TableCell>
                                        <TableCell className="text-blue-600 font-medium">{invoice.invoice_number}</TableCell>
                                        <TableCell>{invoice.description}</TableCell>
                                        <TableCell>
                                            <Badge
                                                className={
                                                    invoice.status === "paid" || invoice.payment_status === "paid"
                                                        ? "bg-green-200 text-green-800 border-green-200"
                                                        : invoice.status === "overdue" || invoice.payment_status === "overdue"
                                                        ? "bg-red-200 text-red-800 border-red-200"
                                                        : invoice.status === "pending" || invoice.payment_status === "pending"
                                                        ? "bg-yellow-200 text-yellow-800 border-yellow-200"
                                                        : "bg-blue-200 text-blue-800 border-blue-200"
                                                }
                                            >
                                                {invoice.payment_status || invoice.status}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>{new Date(invoice.created_at).toLocaleDateString()}</TableCell>
                                        <TableCell>{invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : "N/A"}</TableCell>
                                        <TableCell className="font-medium">
                                            ${invoice.amount.toFixed(2)} {invoice.currency.toUpperCase()}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </Card>
            </div>
        </PageWrapper>
    );
};

export default BillingDetails;
